import * as React from 'react'
import { useMemo } from 'react'
import { useIntl } from 'react-intl'
import { rem } from 'polished'
import styled from 'styled-components'
import clsx from 'clsx'
import Group from 'components/utils/Group'
import TextButton from 'LuxKit/Button/TextButton'
import PriceRowPrice from 'LuxKit/PricePoints/PriceRowPrice'
import PriceRowPriceCaption from 'LuxKit/PricePoints/PriceRowPriceCaption'
import PriceRowAgentHubCommission from 'agentHub/components/PriceRowAgentHubCommission'
import PriceRowLuxPlusPrice from 'LuxKit/PricePoints/LuxPlus/PriceRowLuxPlusPrice'
import PriceRowLuxPlusNonMemberPrice from 'LuxKit/PricePoints/LuxPlus/PriceRowLuxPlusNonMemberPrice'
import LoyaltyPointsEarnWithBreakdownModal from 'loyalty/components/Earn/LoyaltyPointsEarnWithBreakdownModal'
import TripGuard from 'tripPlanner/components/TripGuard/TripGuard'
import { ExperienceBookmarkButton } from 'tripPlanner/components/Bookmark/BookmarkButton'
import { mediaQueryUp } from 'components/utils/breakpoint'
import LineShoppingCartIcon from 'LuxKit/Icons/line/LineShoppingCartIcon'
import { getAgentHubExperienceType } from 'lib/agentHub/agentHubUtils'

const FadingPriceRowPriceCaption = styled(PriceRowPriceCaption)`
  opacity: 0;
  transition: opacity 0.2s;
  pointer-events: none;

  &.visible {
    opacity: 1;
    pointer-events: auto;
  }
`

const BottomContent = styled.div`
  display: grid;
  grid-template-areas:
    "save . ."
    "price . ."
    "buy buy buy";
  grid-template-columns: auto 1fr auto;
  grid-row-gap: ${rem(12)};
  grid-column-gap: ${rem(20)};
  align-items: center;
  ${mediaQueryUp.tablet} {
    grid-template-areas: "price save buy";
  }
`

const PriceGridItem = styled.div`
  grid-area: price;
`

const SaveGridItem = styled.div`
  grid-area: save;

  ${mediaQueryUp.tablet} {
    text-align: right;
  }
`

const BuyGridItem = styled.div`
  grid-area: buy;
`

interface Props {
  experience: App.ExperienceOffer
  totalPrice: number
  totalLuxPlusPrice: number
  isFromPrice: boolean
  showLuxPlusPrice: boolean
  pointsEarnCalculationRequest: Array<App.LoyaltyPointsEarnCalculationRequest | undefined>
  userMultiItemCartSupported: boolean
  onBuyNow: (e: React.MouseEvent<HTMLElement>) => void
  onAddToUserCart: (e: React.MouseEvent<HTMLElement>) => void
  secondaryAction?: React.ReactElement
}

function ExperienceOfferPageBuyBar(props: Props) {
  const {
    experience,
    totalPrice,
    totalLuxPlusPrice,
    isFromPrice,
    showLuxPlusPrice,
    pointsEarnCalculationRequest,
    userMultiItemCartSupported,
    onBuyNow,
    onAddToUserCart,
    secondaryAction,
  } = props
  const intl = useIntl()
  const productType = useMemo(() => getAgentHubExperienceType(experience), [experience])
  const offerLocations = useMemo(() => [experience.location.name], [experience.location.name])

  const fromLabel = intl.formatMessage({
    id: 'GNhke_zVHO9zGHOsqLCn',
    defaultMessage: 'From',
    description: 'Price prefix label indicating the starting price for experience tickets',
  })
  const totalLabel = intl.formatMessage({
    id: 'MGi3hnHzfEbY4NyUM2_2',
    defaultMessage: 'total',
    description: 'Price unit label showing the total price for selected experience tickets',
  })
  const addToCartLabel = intl.formatMessage({
    id: 'sLMdZIi4tcR7FUyZ77YF',
    defaultMessage: 'Add to cart',
    description: 'Button label to add experience tickets to the shopping cart',
  })
  const buyNowLabel = intl.formatMessage({
    id: 'Rl_hV-p1C684QVEDFNiK',
    defaultMessage: 'Buy now',
    description: 'Button label to immediately purchase experience tickets',
  })

  return (
    <BottomContent>
      <PriceGridItem>
        <Group direction="vertical" gap={4}>
          {!showLuxPlusPrice && (
            <Group direction="vertical">
              <FadingPriceRowPriceCaption className={clsx({ visible: isFromPrice })}>
                {fromLabel}
              </FadingPriceRowPriceCaption>
              <PriceRowPrice
                size="M"
                price={totalPrice}
                saleUnit={(experience.ticketUnitLabel && isFromPrice) ? experience.ticketUnitLabel : totalLabel}
              />
              <PriceRowAgentHubCommission
                offerId={experience.id}
                productType={productType}
                offerLocations={offerLocations}
              />
            </Group>
          )}
          {!!showLuxPlusPrice && (
            <Group direction="vertical">
              <FadingPriceRowPriceCaption className={clsx({ visible: isFromPrice })}>
                {fromLabel}
              </FadingPriceRowPriceCaption>
              <PriceRowLuxPlusPrice
                logoPlacement="trailing"
                size="L"
                memberPrice={totalLuxPlusPrice}
                saleUnit={experience.ticketUnitLabel && isFromPrice ? experience.ticketUnitLabel : totalLabel}
              />
              <PriceRowLuxPlusNonMemberPrice
                nonMemberPrice={totalPrice}
              />
            </Group>
          )}
          <LoyaltyPointsEarnWithBreakdownModal
            calculationRequests={pointsEarnCalculationRequest}
            calculationType={isFromPrice ? 'estimate' : 'exact'}
            hideStatusCredits
          />
        </Group>
      </PriceGridItem>
      <TripGuard>
        <SaveGridItem>
          <ExperienceBookmarkButton offer={experience} />
        </SaveGridItem>
      </TripGuard>
      <BuyGridItem>
        <Group direction="vertical-reverse" gap={8} tabletDirection="horizontal">
          {userMultiItemCartSupported && <TextButton
            kind="secondary"
            startIcon={<LineShoppingCartIcon />}
            size="large"
            onClick={onAddToUserCart}
            disabled={experience.status !== 'ONLINE'}
          >
            {addToCartLabel}
          </TextButton>}
          {secondaryAction}
          <TextButton
            kind="primary"
            size="large"
            type="submit"
            onClick={onBuyNow}
            disabled={experience.status !== 'ONLINE'}
          >
            {buyNowLabel}
          </TextButton>
        </Group>
      </BuyGridItem>
    </BottomContent>
  )
}

export default ExperienceOfferPageBuyBar
